import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, 
  X, 
  Sun, 
  Moon, 
  ChevronUp, 
  Mail, 
  Instagram, 
  Facebook, 
  ExternalLink 
} from "lucide-react";
import { ROUTE_PATHS, cn, scrollToSection } from "@/lib/index";
import { Button } from "@/components/ui/button";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  const navItems = [
    { label: "Home", id: "hero" },
    { label: "Why Us", id: "why-choose" },
    { label: "Services", id: "services" },
    { label: "Portfolio", id: "portfolio" },
    { label: "Connect", id: "social-connect" },
    { label: "Reviews", id: "testimonials" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/30 selection:text-primary">
      {/* Navigation Header */}
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b",
          isScrolled
            ? "bg-background/80 backdrop-blur-xl border-border/50 py-3 shadow-lg"
            : "bg-transparent border-transparent py-5"
        )}
      >
        <nav className="container mx-auto px-6 flex items-center justify-between">
          <NavLink
            to={ROUTE_PATHS.HOME}
            className="text-2xl font-bold tracking-tighter bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent hover:opacity-80 transition-opacity"
          >
            Xuvera Agency
          </NavLink>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors cursor-pointer"
              >
                {item.label}
              </button>
            ))}
            <div className="h-4 w-[1px] bg-border/50 mx-2" />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="rounded-full hover:bg-primary/10 hover:text-primary"
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <Button
              onClick={() => scrollToSection("social-connect")}
              className="bg-primary text-primary-foreground hover:shadow-[0_0_20px_rgba(var(--primary),0.4)] transition-all"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Toggle */}
          <div className="flex items-center gap-4 md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="rounded-full"
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <button
              className="text-foreground"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-background/95 backdrop-blur-2xl border-b border-border/50 overflow-hidden"
            >
              <div className="container mx-auto px-6 py-8 flex flex-col gap-6">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      scrollToSection(item.id);
                      setIsMenuOpen(false);
                    }}
                    className="text-lg font-semibold text-left text-muted-foreground hover:text-primary transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
                <Button
                  className="w-full bg-primary py-6 text-lg"
                  onClick={() => {
                    scrollToSection("social-connect");
                    setIsMenuOpen(false);
                  }}
                >
                  Start Your Project
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="relative">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border/50 pt-20 pb-10">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="col-span-1 md:col-span-1">
              <h3 className="text-2xl font-bold tracking-tighter mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Xuvera Agency
              </h3>
              <p className="text-muted-foreground leading-relaxed max-w-xs">
                Empowering global businesses with high-converting websites and 
                intelligent AI automation systems. The future is automated.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-6">Agency</h4>
              <ul className="space-y-4 text-muted-foreground">
                <li><button onClick={() => scrollToSection("why-choose")} className="hover:text-primary transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection("services")} className="hover:text-primary transition-colors">Our Services</button></li>
                <li><button onClick={() => scrollToSection("portfolio")} className="hover:text-primary transition-colors">Success Stories</button></li>
                <li><button onClick={() => scrollToSection("testimonials")} className="hover:text-primary transition-colors">Testimonials</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-6">Resources</h4>
              <ul className="space-y-4 text-muted-foreground">
                <li className="flex items-center gap-2 hover:text-primary cursor-pointer transition-colors">AI Blog <ExternalLink size={14} /></li>
                <li className="flex items-center gap-2 hover:text-primary cursor-pointer transition-colors">Help Center <ExternalLink size={14} /></li>
                <li className="flex items-center gap-2 hover:text-primary cursor-pointer transition-colors">API Documentation <ExternalLink size={14} /></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-6">Connect</h4>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="mailto:xuvera122@gmail.com" 
                  className="p-3 rounded-full bg-destructive/10 text-destructive border border-destructive/20 hover:bg-destructive hover:text-white transition-all shadow-[0_0_15px_rgba(var(--destructive),0.2)]"
                  title="Email Us"
                >
                  <Mail size={20} />
                </a>
                <a href="#" className="p-3 rounded-full bg-secondary text-foreground border border-border/50 hover:bg-primary hover:text-white transition-all">
                  <Instagram size={20} />
                </a>
                <a href="#" className="p-3 rounded-full bg-secondary text-foreground border border-border/50 hover:bg-primary hover:text-white transition-all">
                  <Facebook size={20} />
                </a>
              </div>
              <p className="mt-6 text-sm text-muted-foreground">
                xuvera122@gmail.com
              </p>
            </div>
          </div>

          <div className="pt-10 border-t border-border/50 flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-muted-foreground text-sm">
              &copy; 2026 Xuvera Agency. All rights reserved.
            </p>
            <div className="flex gap-8 text-sm text-muted-foreground">
              <a href="#" className="hover:text-primary transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-primary transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to top */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-8 right-8 z-50 p-4 rounded-full bg-primary text-primary-foreground shadow-2xl hover:scale-110 transition-transform active:scale-95"
          >
            <ChevronUp size={24} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
